import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Field, Loading, NSelect, useFetch } from "@/components/common";
import { WarningsBox, warningsOf } from "@/components/Pleins";
import { ReceiptPicker, FUELS, nowParts } from "@/pages/commerciaux/Plein";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fNum } from "@/lib/api";

const n = (s) => (s === "" || s == null ? null : Number(String(s).replace(",", ".")));
const empty = () => ({ ...nowParts(), vehicle_id: "", station: "", fuel: "", mileage: "", liters: "", amount_ttc: "", comment: "" });

export default function LogiPlein() {
  const ctx = useFetch("/logistique/pleins/context");
  const [f, setF] = useState(empty());
  const [card, setCard] = useState("");
  const [fileId, setFileId] = useState(null);
  const [warn, setWarn] = useState(null);
  const [busy, setBusy] = useState(false);
  if (ctx.loading || !ctx.data) return <Loading />;
  const cards = ctx.data.cards;
  const cardId = card || (cards.length === 1 ? cards[0].id : "");
  const veh = ctx.data.vehicles.find((v) => v.id === f.vehicle_id);
  const set = (k) => (e) => { setF({ ...f, [k]: e?.target ? e.target.value : e }); setWarn(null); };
  const ttc = n(f.amount_ttc), liters = n(f.liters);
  const ready = cardId && f.vehicle_id && f.date && f.time && f.station.trim() && f.fuel && n(f.mileage) > 0 && liters > 0 && ttc > 0 && fileId;
  const send = async (confirm) => {
    setBusy(true);
    try {
      await api.post("/logistique/pleins", { ...f, card_id: cardId, mileage: Math.round(n(f.mileage)), liters, amount_ttc: ttc, file_id: fileId, confirm });
      toast.success("Plein Fleet Pro enregistré");
      setF({ ...empty(), fuel: f.fuel });
      setFileId(null);
      setWarn(null);
      ctx.reload();
    } catch (e) {
      const w = warningsOf(e);
      if (w) setWarn(w);
      else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (!cards.length) return <div className="max-w-xl"><PageHeader overline="Véhicules de livraison · Fleet Pro" title="Enregistrer un plein Fleet Pro" /><p className="text-sm text-slate-500" data-testid="logi-plein-no-card">Aucune carte Fleet Pro logistique n'est rattachée à votre compte. Contactez la Direction.</p></div>;
  return (
    <div className="max-w-xl" data-testid="logi-plein-page">
      <PageHeader overline="Véhicules de livraison · Fleet Pro" title="Enregistrer un plein Fleet Pro" />
      <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 space-y-3 rise">
        {cards.length === 1 ? <div className="font-semibold text-[#0E2D43]" data-testid="logi-plein-holder">Titulaire : {cards[0].holder_name} <span className="ml-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-[#E8F1FB] text-[#1d5fa8]">Fleet Pro</span></div>
          : <Field label="Titulaire de la carte *"><NSelect value={cardId} onChange={setCard} placeholder="Choisir…" testid="logi-plein-card" options={cards.map((c) => ({ value: c.id, label: c.holder_name }))} /></Field>}
        <Field label="Véhicule utilisé *"><NSelect value={f.vehicle_id} onChange={set("vehicle_id")} placeholder="Choisir un véhicule de livraison actif…" testid="logi-plein-vehicle" options={ctx.data.vehicles.map((v) => ({ value: v.id, label: `${v.plate} · ${v.brand || ""} ${v.model || ""}` }))} /></Field>
        {veh?.mileage && <div className="text-xs text-slate-500" data-testid="logi-plein-last-km">Dernier kilométrage connu : {fNum(veh.mileage)} km</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date *"><Input type="date" value={f.date} onChange={set("date")} className="h-11" data-testid="logi-plein-date" /></Field>
          <Field label="Heure *"><Input type="time" value={f.time} onChange={set("time")} className="h-11" data-testid="logi-plein-time" /></Field>
        </div>
        <Field label="Kilométrage compteur *"><Input inputMode="numeric" value={f.mileage} onChange={set("mileage")} className="h-11 text-lg font-semibold" data-testid="logi-plein-mileage" /></Field>
        <Field label="Station *"><Input value={f.station} onChange={set("station")} className="h-11" data-testid="logi-plein-station" /></Field>
        <Field label="Carburant *">
          <div className="flex flex-wrap gap-2">{FUELS.map((x) => (
            <button key={x} type="button" onClick={() => set("fuel")(x)} data-testid={`logi-plein-fuel-${x}`} className={`px-3 h-10 rounded-lg border text-sm font-semibold transition-colors ${f.fuel === x ? "bg-[#0E2D43] text-white border-[#0E2D43]" : "bg-white text-slate-600 border-slate-200"}`}>{x}</button>
          ))}</div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nombre de litres *"><Input inputMode="decimal" value={f.liters} onChange={set("liters")} className="h-11" data-testid="logi-plein-liters" /></Field>
          <Field label="Montant TTC (€) *"><Input inputMode="decimal" value={f.amount_ttc} onChange={set("amount_ttc")} className="h-11" data-testid="logi-plein-ttc" /></Field>
        </div>
        <div className="text-sm" data-testid="logi-plein-ppl">Prix TTC / litre : <b className="text-[#0E2D43]">{ttc && liters ? `${fNum(ttc / liters, 3)} €/L` : "—"}</b></div>
        <Field label="Photo du justificatif *"><ReceiptPicker fileId={fileId} setFileId={setFileId} /></Field>
        <Field label="Commentaire"><Input value={f.comment} onChange={set("comment")} className="h-11" data-testid="logi-plein-comment" /></Field>
        <WarningsBox warnings={warn} testid="logi-plein-warnings" />
        {warn ? (
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-12" onClick={() => setWarn(null)} data-testid="logi-plein-fix">Corriger</Button>
            <Button className="h-12 bg-amber-600 hover:bg-amber-700" disabled={busy} onClick={() => send(true)} data-testid="logi-plein-confirm">Confirmer quand même</Button>
          </div>
        ) : (
          <Button className="w-full h-12 bg-[#F49619] hover:bg-[#E0830B] text-white font-semibold" disabled={!ready || busy} onClick={() => send(false)} data-testid="logi-plein-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer le plein"}
          </Button>
        )}
      </div>
    </div>
  );
}
